import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';

const comparisonData = [
  { section: 'COPIER', rows: [
    { label: 'MT4 / MT5', octopus: 'Yes', others: 'Yes' },
    { label: 'Interactive Brokers', octopus: 'Yes', others: 'No' },
    { label: 'Finalto', octopus: 'Yes', others: 'No' },
    { label: 'Integral', octopus: 'Yes', others: 'No' },
    { label: 'CQG', octopus: 'Yes', others: 'No' },
    { label: 'Basic Copy Settings', octopus: 'Yes', others: 'Yes' },
    { label: 'Advanced Copy Settings', octopus: 'Yes', others: 'Yes' },
    { label: 'Redundancy and fallback integrated', octopus: 'Yes', others: 'Yes' },
    { label: 'HFT - Ultra Fast Execution', octopus: 'Yes', others: 'Yes' },
    { label: 'Consistency validation', octopus: 'Yes', others: 'No' },
  ]},
  { section: 'SUPPORT', rows: [
    { label: 'We speak multiple languages', octopus: 'Yes', others: 'Yes' },
    { label: '24/6', octopus: 'Yes', others: 'No' },
    { label: 'Feature Request Possible', octopus: 'Yes', others: 'No' },
  ]},
  { section: 'PLATFORM', rows: [
    { label: 'All data (trades) record in DataBase', octopus: 'Yes', others: 'No' },
    { label: 'Arquive Accounts', octopus: 'Yes', others: 'No' },
    { label: 'Registered Company', octopus: 'Yes', others: 'Yes' },
    { label: 'Multi-Layer users', octopus: 'Yes', others: 'No' },
    { label: 'Dedicated Instances', octopus: 'Yes', others: 'Yes' },
    { label: 'Never miss a trade', octopus: 'Yes', others: 'No' },
    { label: 'Unlimited Accounts', octopus: 'Yes', others: 'Yes' },
    { label: 'Choose region server', octopus: 'Yes', others: 'Yes' },
    { label: 'Working in Cluster', octopus: 'Yes', others: 'No' },
    { label: 'Dedicated IPs', octopus: 'Yes', others: 'Yes' },
    { label: 'Free unlimited API calls', octopus: 'Yes', others: 'Yes' },
  ]},
];

const CustomComparisonTable = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const gridTemplateColumns = isSmall ? '1.2fr 0.9fr 0.9fr' : '2fr 1fr 1fr';
  const rowFontSize = isSmall ? 14 : 16;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        borderRadius: 1,
        boxShadow: 6,
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        mb: 6,
      }}
    >
      {/* Main Header - visually separated and styled, grid matches table columns */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: { xs: 2, sm: 4 },
          px: { xs: 1.5, sm: 4 },
          mb: 2,
          borderRadius: 1,
          boxShadow: 3,
          columnGap: 0
        }}
      >
        <Typography
          variant="h5"
          sx={{ 
            fontWeight: 800, 
            letterSpacing: { xs: 0, sm: 2 }, 
            textShadow: '0 2px 8px rgba(0,0,0,0.08)', 
            pl: { xs: 0.5, sm: 1 }, 
            alignSelf: 'center',
            fontSize: { xs: '1rem', sm: '1.5rem' }
          }}
        >
          Comparison
        </Typography>
        <Typography
          variant="h6"
          sx={{ 
            fontWeight: 700, 
            letterSpacing: { xs: 0.5, sm: 1 }, 
            textAlign: 'center', 
            alignSelf: 'center', 
            borderLeft: { sm: `2px solid ${theme.palette.primary.contrastText}` }, 
            borderRight: { sm: `2px solid ${theme.palette.primary.contrastText}` },
            fontSize: { xs: '0.85rem', sm: '1rem' },
            px: { xs: 0.5, sm: 1 }
          }}
        >
          OctopusSync
        </Typography>
        <Typography
          variant="h6"
          sx={{ 
            fontWeight: 700, 
            letterSpacing: { xs: 0.5, sm: 1 }, 
            textAlign: 'center', 
            alignSelf: 'center',
            fontSize: { xs: '0.85rem', sm: '1rem' },
            px: { xs: 0.5, sm: 1 }
          }}
        >
          Others
        </Typography>
      </Box>
      {comparisonData.map((section, sIdx) => (
        <React.Fragment key={section.section}>
          <Box sx={{ bgcolor: theme.palette.grey[100], px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{section.section}</Typography>
          </Box>
          {section.rows.map((row, rIdx) => (
            <Box
              key={row.label}
              component={motion.div}
              initial={false}
              whileHover={{ scale: 1.01, boxShadow: '0 2px 12px 0 rgba(101,17,175,0.08)' }}
              transition={{ duration: 0.2 }}
              sx={{
                display: 'grid',
                gridTemplateColumns,
                alignItems: 'center',
                px: { xs: 2, sm: 3 },
                py: { xs: 1.2, sm: 1.6 },
                borderBottom: rIdx === section.rows.length - 1 && sIdx === comparisonData.length - 1 ? 'none' : `1px solid ${theme.palette.divider}`,
                background: theme.palette.background.paper,
                fontSize: rowFontSize,
                columnGap: 0,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.label}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {row.octopus === 'Yes' ? (
                  <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 700 }}>-</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {row.others === 'Yes' ? (
                  <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 700 }}>-</Typography>
                )}
              </Box>
            </Box>
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default CustomComparisonTable;
